import { toRegex } from "diacritic-regex";
import * as _ from "lodash";
import moment from "moment";
import {
  MANDATORY_DOCUMENT_TYPES_FOR_DISPATCH,
  MANDATORY_DOCUMENT_TYPES_FOR_HIL_SUBMISSION
} from "../../../webapp-commons/data";
import {
  DEFAULT_PROJECT_FILTERS_SETTINGS,
  DOCUMENT_TYPES
} from "../data/DefaultSettings";
import { fetchHil, getDiff } from "../utils";
import { createSyncHandler } from "./createSyncHandler";

let NEXT_QUERY = 0;

export default {
  namespaced: true,
  state: {
    all: [],
    current: null,
    previous: null,
    columns: [],
    filters: DEFAULT_PROJECT_FILTERS_SETTINGS,
    proposals: [],
    proposalsLoaded: false,
    documents: [],
    documentsLoaded: false,
    files: [],
    filesLoaded: false
  },
  getters: {
    docStatus: state => {
      if (!state.documentsLoaded) {
        return { result: "loading" };
      }

      const exclusions = [
        "accord_employeur" // Exclude accord_employeur from this check as it should only be mandatory for hil submission
      ];
      const incompleteDocuments = state.documents.filter(
        d =>
          d.type === "file" &&
          d.driveId.length === 0 &&
          !exclusions.includes(d.title)
      );
      if (incompleteDocuments.length !== 0) {
        return {
          result: "incomplete",
          reasons: incompleteDocuments.map(doc => {
            const find = DOCUMENT_TYPES.find(d => d.value === doc.title);
            return find ? find.text : doc.title;
          })
        };
      }

      for (const k of Object.keys(
        MANDATORY_DOCUMENT_TYPES_FOR_HIL_SUBMISSION
      )) {
        let found = false;
        const doc = MANDATORY_DOCUMENT_TYPES_FOR_HIL_SUBMISSION[k];
        for (const type of doc.types) {
          for (const d of state.documents) {
            if (d.title === type && d.driveId.length > 0) {
              found = true;
              break;
            }
          }

          /**
           * Error occurs if it is not found and if that category cannot be overridden
           * or if the override is false
           */
          if (
            !found &&
            (!doc.override ||
              !state.current.overrides?.[doc.override.property]?.date)
          ) {
            return {
              result: "incomplete-submission",
              reasons: [k],
              override: doc.override
            };
          }
        }
      }

      return { result: "ok" };
    },

    docStatusForDispatch: state => {
      if (!state.documentsLoaded) {
        return "loading";
      }

      // All documents from all types must be filled
      for (const k of Object.keys(MANDATORY_DOCUMENT_TYPES_FOR_DISPATCH)) {
        for (const rd of MANDATORY_DOCUMENT_TYPES_FOR_DISPATCH[k]) {
          for (const d of state.documents) {
            if (d.title === rd && d.driveId.length === 0) {
              return k;
            }
          }
        }
      }

      return "ok";
    },

    likedProposals: state => {
      if (state.proposalsLoaded) {
        return state.proposals.filter(
          p =>
            p.liked &&
            (!p.disliked || moment(p.liked).isAfter(moment(p.disliked)))
        );
      }

      return null;
    },

    dislikedProposals: state => {
      if (state.proposalsLoaded) {
        return state.proposals.filter(
          p =>
            p.disliked &&
            (!p.liked || moment(p.disliked).isAfter(moment(p.liked)))
        );
      }

      return null;
    },

    proposalsWithVisits: state => {
      if (state.proposalsLoaded) {
        return state.proposals.filter(
          p => p.visited != null || p.visitCancelled === true
        );
      }

      return null;
    },

    filtered: state => {
      const search = state.filters.search.toLowerCase();
      const searchRegex = toRegex({ flags: "i" })(
        _.escapeRegExp(state.filters.search)
      );
      return state.all.filter(prj => {
        if (search !== "") {
          if (search === "??o") {
            return prj.assignees.reloc == null || prj.assignees.reloc === "";
          } else if (search === "??c") {
            return prj.assignees.sale == null || prj.assignees.sale === "";
          } else {
            return (
              `${prj.firstName} ${prj.lastName}`
                ?.toLowerCase()
                .includes(search) ||
              prj._id.includes(search) ||
              prj.userId?.toLowerCase().includes(search) ||
              _.some(prj.assignees, v => v.toLowerCase() === search.trim()) ||
              prj.company?.toLowerCase().includes(search) ||
              prj.sponsor?.toLowerCase().includes(search) ||
              prj.school?.toLowerCase().includes(search) ||
              prj.fromAddress?.toLowerCase().includes(search) ||
              prj.addressWork?.toLowerCase().includes(search) ||
              prj.phoneNumber?.replace(/\s+/g, "").includes(search) ||
              prj.invoiceRef?.toLowerCase() === search ||
              prj.tags?.some(tag => searchRegex.test(tag))
            );
          }
        }
        return true;
      });
    },

    filteredGroupByProgress: (state, getters) => {
      return _(getters.filtered)
        .groupBy("currentProgress")
        .mapValues(leases =>
          // Sort by latest column change, oldest first
          _.sortBy(leases, l =>
            _(l.progress)
              .values()
              .max()
          )
        )
        .value();
    }
  },
  mutations: {
    reset(state) {
      state.all = [];
      state.filtered = [];
    },

    setProjects(state, { projects, filterSettings }) {
      if (projects != null) {
        state.all = projects;
      }
    },

    setProject(state, project) {
      state.current = _.cloneDeep(project);
      state.proposals = [];
      state.proposalsLoaded = false;
      state.previous = _.cloneDeep(project);
    },

    setSyncResults(state, { resp: project, updatedKeys }) {
      if (
        project.mandateId != null &&
        project.mandateId !== state.previous.mandateId
      ) {
        state.current.mandateId = project.mandateId;
        window.location.reload();
      }
      const billingDiff = getDiff(project.billing, state.current.billing);
      if (
        state.previous.billing == null &&
        Object.keys(billingDiff).length > 0
      ) {
        window.location.reload();
      }
      state.current = {
        ...state.current,
        _mbYear: project._mbYear,
        adminYear: project.adminYear,
        progress: _.cloneDeep(project.progress),
        currentProgress: _.cloneDeep(project.currentProgress),
        activeAlerts: _.cloneDeep(project.activeAlerts),
        assignees: _.cloneDeep(project.assignees)
      };
      state.previous = _.cloneDeep(project);
    },

    setProposals(state, proposals) {
      state.proposals = proposals;
      state.proposalsLoaded = true;
    },

    setDocuments(state, documents) {
      state.documents = documents;
      state.documentsLoaded = true;
    },

    setFiles(state, files) {
      state.files = files;
      state.filesLoaded = true;
    },

    setFilterSearch(state, search) {
      state.filters.search = search;
    },

    setFilterFilter(state, filter) {
      state.filters.filter = filter;
    },

    setFilterColumn(state, column) {
      state.filters.column = column;
    },

    setFilterYear(state, year) {
      state.filters.year = year;
    },

    setColumns(state, columns) {
      state.columns = columns;
    }
  },
  actions: {
    async list({ state, commit }) {
      commit("reset");
      const per = 200;
      let count = 0;
      const properties = [
        "progress",
        "firstName",
        "lastName",
        "assignees",
        "activeAlerts",
        "userId",
        "dateMove",
        "toDelete",
        "dateMove",
        "alerts",
        "alertsSkipped",
        "company",
        "sponsor",
        "school",
        "fromAddress",
        "addressWork",
        "invoiceRef",
        "phoneNumber",
        "companyId",
        "tags",
        "signatures.step",
        "serviceWish"
      ].join(" ");

      const qid = ++NEXT_QUERY;
      const projects = [];

      // eslint-disable-next-line no-constant-condition -- The exit condition is in the loop
      while (true) {
        if (NEXT_QUERY !== qid) {
          return;
        }

        const resp = await (
          await fetchHil(`/leases`, {
            method: "POST",
            body: {
              offset: count,
              year: state.filters.year,
              filter: state.filters.filter,
              progress: state.columns,
              size: per,
              properties
            }
          })
        ).json();

        projects.push(...resp);

        if (NEXT_QUERY === qid && resp.length < per) {
          commit("setProjects", { projects });
          break;
        }

        count += per;
      }
    },

    async fetchProject({ commit, dispatch }, { id }) {
      const resp = await fetchHil(`/lease/${id}`);
      if (!resp.ok) {
        await Promise.all([
          commit("setProject", null),
          commit("setProposals", []),
          commit("setDocuments", [])
        ]);
        return;
      }

      const lease = await resp.json();
      await commit("setProject", lease);
      await Promise.all([
        dispatch("fetchProposals", { id }),
        dispatch("fetchDocuments", { id })
      ]);
    },

    async fetchProposals({ commit }, { id }) {
      const resp = await (await fetchHil(`/proposals/lease/${id}`)).json();
      commit("setProposals", resp);
    },

    async fetchDocuments({ commit }, { id }) {
      const resp = await (await fetchHil(`/documents/for/${id}`)).json();
      commit("setDocuments", resp);
    },

    async fetchFiles({ commit }, { id }) {
      const resp = await (await fetchHil(`/cache/for/${id}`)).json();
      commit("setFiles", resp);
    },

    syncProject: createSyncHandler(
      state => `/lease/${state.current._id}`,
      "setSyncResults",
      [
        "_id",
        "userId",
        "tasks",
        "modelVersion",
        "lastProposal",
        "lastDispatch",
        "lastReport",
        "alerts",
        "__v",
        "stats",
        "_mbYear",
        "adminYear",
        "billing"
      ]
    )
  }
};
